import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { FiChevronLeft } from 'react-icons/fi';
import { useUserStore } from '../../stores/userStore';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { useColorScheme } from '../../contexts/ColorSchemeContext';
import baseline from '../../app-ui/baseline';
import copy from './copy.json';
import './Settings.css';
export const unwrap = (r) => r?.data?.data ?? r?.data;
export function AccountBoundary({ component: Component }) {
  const [session, setSession] = useState(() => ({
    state: useUserStore.getState(),
    token: localStorage.getItem('POSTPOP_AUTH_TOKEN'),
    revision: 0,
  }));
  const sync = useCallback(() => {
    const state = useUserStore.getState();
    setSession((previous) => {
      const token = localStorage.getItem('POSTPOP_AUTH_TOKEN');
      if (state === previous.state && token === previous.token) return previous;
      const before = previous.state;
      const changed =
        state.user?.id !== before.user?.id ||
        state.isLogin !== before.isLogin ||
        state.isAdmin !== before.isAdmin ||
        token !== previous.token;
      return { state, token, revision: previous.revision + (changed ? 1 : 0) };
    });
  }, []);
  useEffect(() => {
    const off = useUserStore.subscribe(sync);
    sync();
    return off;
  }, [sync]);
  // Opt-in submission check: storage can change before the user store publishes it.
  const validateSession = () => {
    const state = useUserStore.getState();
    const current =
      state.user?.id === session.state.user?.id &&
      state.isLogin === session.state.isLogin &&
      state.isAdmin === session.state.isAdmin &&
      localStorage.getItem('POSTPOP_AUTH_TOKEN') === session.token;
    if (!current) sync();
    return current;
  };
  if (!session.state.user && session.state.loading) return null;
  return (
    <Component key={session.revision} user={session.state.user} validateSession={validateSession} />
  );
}
export function useSettings() {
  const { locale, setLocale } = useWebLocale(),
    { resolvedMode } = useColorScheme();
  const en = locale === 'en',
    dark = resolvedMode === 'dark',
    colors = baseline.tokens[dark ? 'darkColors' : 'lightColors'];
  const t = (key, vars = {}) =>
    String(copy[en ? 'en' : 'ko'][key] || key).replace(/\{\{(\w+)\}\}/g, (_, k) => vars[k] ?? '');
  return {
    en,
    dark,
    t,
    locale,
    setLocale,
    style: {
      '--settings-text': colors.text,
      '--settings-muted': colors.textSecondary,
      '--settings-border': colors.divider,
      '--settings-surface': colors.surfaceSoft,
      '--settings-background': colors.background,
    },
  };
}
export function SettingsHeader({ title, busy = false }) {
  const nav = useNavigate(),
    { t } = useSettings();
  return (
    <header className="pp-settings-header">
      <button
        type="button"
        aria-label={t('common.back')}
        disabled={busy}
        onClick={() => (window.history.state?.idx > 0 ? nav(-1) : nav('/mypage'))}
      >
        <FiChevronLeft size={22} />
      </button>
      <h1>{title}</h1>
    </header>
  );
}
export function Confirm({ title, body, confirm, cancel, busy, onCancel, onConfirm }) {
  const id = React.useId();
  return (
    <Dialog
      open
      className="pp-review-dialog"
      fullWidth
      maxWidth="xs"
      onClose={() => {
        if (!busy) onCancel();
      }}
      aria-labelledby={id}
    >
      <DialogTitle id={id}>{title}</DialogTitle>
      <DialogContent>
        <p>{body}</p>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} autoFocus onClick={onCancel}>
          {cancel}
        </Button>
        <Button disabled={busy} onClick={onConfirm}>
          {confirm}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
export function useRequestScope(user) {
  const ref = useRef({ alive: true, revision: 0, controllers: new Set() });
  useEffect(() => {
    const s = ref.current;
    s.alive = true;
    const abort = () => {
      s.revision++;
      s.controllers.forEach((c) => c.abort());
    };
    const off = useUserStore.subscribe((a, b) => {
      if (a.user?.id !== b.user?.id || a.isLogin !== b.isLogin || a.isAdmin !== b.isAdmin) abort();
    });
    return () => {
      s.alive = false;
      abort();
      off();
    };
  }, []);
  return () => {
    const s = ref.current,
      revision = s.revision,
      token = localStorage.getItem('POSTPOP_AUTH_TOKEN'),
      c = new AbortController();
    s.controllers.add(c);
    const current = () =>
      s.alive &&
      revision === s.revision &&
      !c.signal.aborted &&
      useUserStore.getState().user?.id === user?.id &&
      localStorage.getItem('POSTPOP_AUTH_TOKEN') === token;
    return {
      current,
      options: { signal: c.signal, suppressGlobalError: true, isSessionCurrent: current },
      cancel: () => c.abort(),
      finish: () => s.controllers.delete(c),
      mounted: () => s.alive && revision === s.revision,
    };
  };
}
